import React from 'react';
import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const wrap = (Icon: LucideIcon) =>
  React.forwardRef<SVGSVGElement, React.ComponentPropsWithoutRef<'svg'>>((props, ref) => (
    <Icon ref={ref} {...props} />
  ));



const iconMap: Record<string, LucideIcon> = {

  AccessTime: LucideIcons.Clock,
  AccessTimeIcon: LucideIcons.Clock,
  Add: LucideIcons.Plus,
  AddIcon: LucideIcons.Plus,
  ArrowDownward: LucideIcons.ArrowDown,
  ArrowDownwardIcon: LucideIcons.ArrowDown,
  ArrowForward: LucideIcons.ArrowRight,
  ArrowForwardIcon: LucideIcons.ArrowRight,
  ArrowUpward: LucideIcons.ArrowUp,
  ArrowUpwardIcon: LucideIcons.ArrowUp,
  Backup: LucideIcons.Cloud,
  BackupIcon: LucideIcons.Cloud,
  BarChart: LucideIcons.TrendingUp,
  BarChartIcon: LucideIcons.TrendingUp,
  BooleanIcon: LucideIcons.ToggleLeft,
  Cancel: LucideIcons.X,
  CancelIcon: LucideIcons.X,
  Check: LucideIcons.Check,
  CheckBox: LucideIcons.CheckSquare,
  CheckBoxIcon: LucideIcons.CheckSquare,
  CheckCircle: LucideIcons.CheckCircle,
  CheckCircleIcon: LucideIcons.CheckCircle,
  CheckIcon: LucideIcons.Check,
  Close: LucideIcons.X,
  CloseIcon: LucideIcons.X,
  CloudUpload: LucideIcons.CloudUpload,
  CloudUploadIcon: LucideIcons.CloudUpload,
  Cube: LucideIcons.Square,
  CubeIcon: LucideIcons.Square,
  Delete: LucideIcons.Trash2,
  DeleteIcon: LucideIcons.Trash2,
  Description: LucideIcons.FileText,
  DescriptionIcon: LucideIcons.FileText,
  DragIndicator: LucideIcons.GripVertical,
  DragIndicatorIcon: LucideIcons.GripVertical,
  Edit: LucideIcons.Pencil,
  EditIcon: LucideIcons.Pencil,
  EditNote: LucideIcons.Pencil,
  EditNoteIcon: LucideIcons.Pencil,
  Error: LucideIcons.AlertTriangle,
  ErrorIcon: LucideIcons.AlertTriangle,
  ExpandMore: LucideIcons.ChevronDown,
  ExpandMoreIcon: LucideIcons.ChevronDown,
  Favorite: LucideIcons.Heart,
  FavoriteIcon: LucideIcons.Heart,
  Flip: LucideIcons.RotateCcw,
  FlipIcon: LucideIcons.RotateCcw,
  Help: LucideIcons.HelpCircle,
  HelpIcon: LucideIcons.HelpCircle,
  Info: LucideIcons.Info,
  InfoIcon: LucideIcons.Info,
  LocationOn: LucideIcons.MapPin,
  LocationOnIcon: LucideIcons.MapPin,
  MenuBook: LucideIcons.FileText,
  MenuBookIcon: LucideIcons.FileText,
  Mic: LucideIcons.Mic,
  MicIcon: LucideIcons.Mic,
  MicNone: LucideIcons.Mic,
  MicNoneIcon: LucideIcons.Mic,
  MultipleChoice: LucideIcons.Circle,
  MultipleChoiceIcon: LucideIcons.Circle,
  MultipleSelect: LucideIcons.CheckSquare,
  MultipleSelectIcon: LucideIcons.CheckSquare,
  Pause: LucideIcons.Pause,
  PauseIcon: LucideIcons.Pause,
  Person: LucideIcons.User,
  PersonIcon: LucideIcons.User,
  PersonOutline: LucideIcons.User,
  PersonOutlineIcon: LucideIcons.User,
  PictureAsPdf: LucideIcons.FileText,
  PictureAsPdfIcon: LucideIcons.FileText,
  PlayArrow: LucideIcons.Play,
  PlayArrowIcon: LucideIcons.Play,
  PlayCircle: LucideIcons.PlayCircle,
  PlayCircleIcon: LucideIcons.PlayCircle,
  PlayCircleOutline: LucideIcons.PlayCircle,
  PlayCircleOutlineIcon: LucideIcons.PlayCircle,
  Preview: LucideIcons.Eye,
  PreviewIcon: LucideIcons.Eye,
  Psychology: LucideIcons.Brain,
  PsychologyIcon: LucideIcons.Brain,
  RadioButtonChecked: LucideIcons.Circle,
  RadioButtonCheckedIcon: LucideIcons.Circle,
  Refresh: LucideIcons.RefreshCw,
  RefreshIcon: LucideIcons.RefreshCw,
  Replay: LucideIcons.RotateCcw,
  ReplayIcon: LucideIcons.RotateCcw,
  Reveal: LucideIcons.Eye,
  RevealIcon: LucideIcons.Eye,
  Save: LucideIcons.Save,
  SaveIcon: LucideIcons.Save,
  School: LucideIcons.GraduationCap,
  SchoolIcon: LucideIcons.GraduationCap,
  Search: LucideIcons.Search,
  SearchIcon: LucideIcons.Search,
  Star: LucideIcons.Star,
  StarIcon: LucideIcons.Star,
  Stop: LucideIcons.Square,
  StopIcon: LucideIcons.Square,
  TextFields: LucideIcons.Type,
  TextFieldsIcon: LucideIcons.Type,
  ToggleOn: LucideIcons.ToggleLeft,
  ToggleOnIcon: LucideIcons.ToggleLeft,
  TrendingUp: LucideIcons.TrendingUp,
  TrendingUpIcon: LucideIcons.TrendingUp,
  Upload: LucideIcons.Upload,
  UploadFile: LucideIcons.Upload,
  UploadFileIcon: LucideIcons.Upload,
  UploadIcon: LucideIcons.Upload,
  Video: LucideIcons.PlayCircle,
  VideoIcon: LucideIcons.PlayCircle,
  Visibility: LucideIcons.Eye,
  VisibilityIcon: LucideIcons.Eye,
};


const wrapped = Object.fromEntries(
  Object.entries(iconMap).map(([name, Icon]) => [name, wrap(Icon)])
) as Record<string, ReturnType<typeof wrap>>;


export default wrapped;


export const AccessTime = wrapped['AccessTime'];
export const AccessTimeIcon = wrapped['AccessTimeIcon'];
export const Add = wrapped['Add'];
export const AddIcon = wrapped['AddIcon'];
export const ArrowDownward = wrapped['ArrowDownward'];
export const ArrowDownwardIcon = wrapped['ArrowDownwardIcon'];
export const ArrowForward = wrapped['ArrowForward'];
export const ArrowForwardIcon = wrapped['ArrowForwardIcon'];
export const ArrowUpward = wrapped['ArrowUpward'];
export const ArrowUpwardIcon = wrapped['ArrowUpwardIcon'];
export const Backup = wrapped['Backup'];
export const BackupIcon = wrapped['BackupIcon'];
export const BarChart = wrapped['BarChart'];
export const BarChartIcon = wrapped['BarChartIcon'];
export const BooleanIcon = wrapped['BooleanIcon'];
export const Cancel = wrapped['Cancel'];
export const CancelIcon = wrapped['CancelIcon'];
export const Check = wrapped['Check'];
export const CheckBox = wrapped['CheckBox'];
export const CheckBoxIcon = wrapped['CheckBoxIcon'];
export const CheckCircle = wrapped['CheckCircle'];
export const CheckCircleIcon = wrapped['CheckCircleIcon'];
export const CheckIcon = wrapped['CheckIcon'];
export const Close = wrapped['Close'];
export const CloseIcon = wrapped['CloseIcon'];
export const CloudUpload = wrapped['CloudUpload'];
export const CloudUploadIcon = wrapped['CloudUploadIcon'];
export const Cube = wrapped['Cube'];
export const CubeIcon = wrapped['CubeIcon'];
export const Delete = wrapped['Delete'];
export const DeleteIcon = wrapped['DeleteIcon'];
export const Description = wrapped['Description'];
export const DescriptionIcon = wrapped['DescriptionIcon'];
export const DragIndicator = wrapped['DragIndicator'];
export const DragIndicatorIcon = wrapped['DragIndicatorIcon'];
export const Edit = wrapped['Edit'];
export const EditIcon = wrapped['EditIcon'];
export const EditNote = wrapped['EditNote'];
export const EditNoteIcon = wrapped['EditNoteIcon'];
export const Error = wrapped['Error'];
export const ErrorIcon = wrapped['ErrorIcon'];
export const ExpandMore = wrapped['ExpandMore'];
export const ExpandMoreIcon = wrapped['ExpandMoreIcon'];
export const Favorite = wrapped['Favorite'];
export const FavoriteIcon = wrapped['FavoriteIcon'];
export const Flip = wrapped['Flip'];
export const FlipIcon = wrapped['FlipIcon'];
export const Help = wrapped['Help'];
export const HelpIcon = wrapped['HelpIcon'];
export const Info = wrapped['Info'];
export const InfoIcon = wrapped['InfoIcon'];
export const LocationOn = wrapped['LocationOn'];
export const LocationOnIcon = wrapped['LocationOnIcon'];
export const MenuBook = wrapped['MenuBook'];
export const MenuBookIcon = wrapped['MenuBookIcon'];
export const Mic = wrapped['Mic'];
export const MicIcon = wrapped['MicIcon'];
export const MicNone = wrapped['MicNone'];
export const MicNoneIcon = wrapped['MicNoneIcon'];
export const MultipleChoice = wrapped['MultipleChoice'];
export const MultipleChoiceIcon = wrapped['MultipleChoiceIcon'];
export const MultipleSelect = wrapped['MultipleSelect'];
export const MultipleSelectIcon = wrapped['MultipleSelectIcon'];
export const Pause = wrapped['Pause'];
export const PauseIcon = wrapped['PauseIcon'];
export const Person = wrapped['Person'];
export const PersonIcon = wrapped['PersonIcon'];
export const PersonOutline = wrapped['PersonOutline'];
export const PersonOutlineIcon = wrapped['PersonOutlineIcon'];
export const PictureAsPdf = wrapped['PictureAsPdf'];
export const PictureAsPdfIcon = wrapped['PictureAsPdfIcon'];
export const PlayArrow = wrapped['PlayArrow'];
export const PlayArrowIcon = wrapped['PlayArrowIcon'];
export const PlayCircle = wrapped['PlayCircle'];
export const PlayCircleIcon = wrapped['PlayCircleIcon'];
export const PlayCircleOutline = wrapped['PlayCircleOutline'];
export const PlayCircleOutlineIcon = wrapped['PlayCircleOutlineIcon'];
export const Preview = wrapped['Preview'];
export const PreviewIcon = wrapped['PreviewIcon'];
export const Psychology = wrapped['Psychology'];
export const PsychologyIcon = wrapped['PsychologyIcon'];
export const RadioButtonChecked = wrapped['RadioButtonChecked'];
export const RadioButtonCheckedIcon = wrapped['RadioButtonCheckedIcon'];
export const Refresh = wrapped['Refresh'];
export const RefreshIcon = wrapped['RefreshIcon'];
export const Replay = wrapped['Replay'];
export const ReplayIcon = wrapped['ReplayIcon'];
export const Reveal = wrapped['Reveal'];
export const RevealIcon = wrapped['RevealIcon'];
export const Save = wrapped['Save'];
export const SaveIcon = wrapped['SaveIcon'];
export const School = wrapped['School'];
export const SchoolIcon = wrapped['SchoolIcon'];
export const Search = wrapped['Search'];
export const SearchIcon = wrapped['SearchIcon'];
export const Star = wrapped['Star'];
export const StarIcon = wrapped['StarIcon'];
export const Stop = wrapped['Stop'];
export const StopIcon = wrapped['StopIcon'];
export const TextFields = wrapped['TextFields'];
export const TextFieldsIcon = wrapped['TextFieldsIcon'];
export const ToggleOn = wrapped['ToggleOn'];
export const ToggleOnIcon = wrapped['ToggleOnIcon'];
export const TrendingUp = wrapped['TrendingUp'];
export const TrendingUpIcon = wrapped['TrendingUpIcon'];
export const Upload = wrapped['Upload'];
export const UploadFile = wrapped['UploadFile'];
export const UploadFileIcon = wrapped['UploadFileIcon'];
export const UploadIcon = wrapped['UploadIcon'];
export const Video = wrapped['Video'];
export const VideoIcon = wrapped['VideoIcon'];
export const Visibility = wrapped['Visibility'];
export const VisibilityIcon = wrapped['VisibilityIcon'];
